import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import HorizontalScrollList from "../../../../components/HorizontalScrollList";
import { TRANSLATION_KEY_CATEGORY_CAROUSEL } from "../../../../components/HorizontalScrollList/constants";
import { withTranslation } from "../../../../components/translation";
import GuidedSearchBoardTile from "./GuidedSearchBoardTile";
import styles from "./GuidedSearchBoard.css";

const EMPTY_ARRAY = [];

const normalisePathname = (pathname) => {
  if (!pathname) {
    return "/";
  }

  return pathname.replace(/\/+$/, "") || "/";
};

const normaliseComparableValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : undefined;

const getLinkMetadata = (linkUrl) => {
  if (!linkUrl) {
    return { pathname: "/", q: undefined, cid: undefined };
  }

  const withoutDomain = linkUrl.replace(/^https?:\/\/[^/]+/i, "");
  const [pathname, queryString = ""] = withoutDomain.split("?");
  const params = new URLSearchParams(queryString);

  return {
    pathname: normalisePathname(pathname || ""),
    q: normaliseComparableValue(params.get("q")),
    cid: params.get("cid"),
  };
};

const getSelectedCategoryId = (
  guidedSearchCategories,
  currentPath,
  currentSearch,
) => {
  if (!guidedSearchCategories || guidedSearchCategories.length === 0) {
    return undefined;
  }

  const normalisedCurrentPath = normalisePathname(currentPath);
  const searchParams = new URLSearchParams(currentSearch || "");
  const currentQ = normaliseComparableValue(searchParams.get("q"));
  const currentCid = searchParams.get("cid");

  const activeCategory = guidedSearchCategories.find((category) => {
    const { pathname, q, cid } = getLinkMetadata(category.linkUrl);

    if (pathname !== normalisedCurrentPath) {
      return false;
    }

    if (q != null) {
      return q === currentQ;
    }

    if (cid != null) {
      return currentCid == null || cid === currentCid;
    }

    return true;
  });

  if (activeCategory?.selectionKey != null) {
    return activeCategory.selectionKey;
  }

  return undefined;
};

export const GuidedSearchBoard = ({
  guidedSearchCategories,
  currentPath,
  currentSearch,
  description,
  formatTranslation,
}) => {
  const categories = guidedSearchCategories || EMPTY_ARRAY;

  const [selectedCategoryId, setSelectedCategoryId] = useState(() =>
    getSelectedCategoryId(categories, currentPath, currentSearch),
  );
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    setIsDismissed(false);
  }, [currentPath, currentSearch]);

  useEffect(() => {
    setSelectedCategoryId(
      getSelectedCategoryId(categories, currentPath, currentSearch),
    );
  }, [categories, currentPath, currentSearch]);

  if (categories.length === 0 || isDismissed) {
    return null;
  }

  const sectionAriaLabel = formatTranslation(TRANSLATION_KEY_CATEGORY_CAROUSEL);

  return (
    <section
      className={styles.wrapper}
      aria-label={sectionAriaLabel}
      data-guided-search-section="true"
    >
      <HorizontalScrollList
        className={styles.scrollList}
        isCentralised={false}
        scrollSnapping
        ariaLabel={TRANSLATION_KEY_CATEGORY_CAROUSEL}
        visualCategoriesDescription={description}
        selectedItemSelector='[data-guided-search-selected="true"]'
        selectedItemDependency={selectedCategoryId}
      >
        {categories.map((category) => (
          <GuidedSearchBoardTile
            key={category.selectionKey}
            {...category}
            isSelected={selectedCategoryId === category.selectionKey}
            onSelect={(selectionKey) => {
              setSelectedCategoryId(selectionKey);
              setIsDismissed(true);
            }}
          />
        ))}
      </HorizontalScrollList>
    </section>
  );
};

GuidedSearchBoard.propTypes = {
  formatTranslation: PropTypes.func.isRequired,
  guidedSearchCategories: PropTypes.arrayOf(
    PropTypes.shape({
      selectionKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
      name: PropTypes.string.isRequired,
      imageUrl: PropTypes.string.isRequired,
      linkUrl: PropTypes.string.isRequired,
      imageUrls: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  currentPath: PropTypes.string,
  currentSearch: PropTypes.string,
  description: PropTypes.node,
};

export default withTranslation(GuidedSearchBoard);
